import React, { useState, useMemo, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

const Classrooms = () => {
    const basePath = '/admin/classrooms';

    // State Management
    const [classrooms, setClassrooms] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedClassroom, setSelectedClassroom] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [floorFilter, setFloorFilter] = useState('all');

    // Form States
    const [formData, setFormData] = useState({
        name: '',
        floor: '',
    });
    
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [flash, setFlash] = useState(null);

    // Handle flash messages
    useEffect(() => {
        const fetchClassrooms = async () => {
            try {
                const res = await axiosInstance.get(basePath);
                const list = res.data?.classrooms || [];
                setClassrooms(list);
            } catch (error) {
                console.error('Error fetching classrooms:', error);
                setFlash({ error: 'Failed to load classrooms' });
            }
        };

        fetchClassrooms();
    }, [basePath]);

    // Handle flash messages
    useEffect(() => {
        if (flash?.success) {
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: flash.success,
                confirmButtonColor: '#4f46e5',
            });
        }
        if (flash?.error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: flash.error,
                confirmButtonColor: '#ef4444',
            });
        }
    }, [flash]);

    // Get unique floors for filter
    const uniqueFloors = useMemo(() => {
        const floors = [...new Set(classrooms.map(c => c.floor))];
        return floors.sort();
    }, [classrooms]);

    // Search and Filter Logic
    const filteredClassrooms = useMemo(() => {
        return classrooms.filter(classroom => {
            const matchesSearch = 
                classroom.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                classroom.floor.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesFloor = floorFilter === 'all' || classroom.floor === floorFilter;
            
            return matchesSearch && matchesFloor;
        });
    }, [classrooms, searchQuery, floorFilter]);

    // Stats Calculation
    const stats = useMemo(() => ({
        total: classrooms.length,
        byFloor: uniqueFloors.reduce((acc, floor) => {
            acc[floor] = classrooms.filter(c => c.floor === floor).length;
            return acc;
        }, {}),
    }), [classrooms, uniqueFloors]);

    // Form Validation
    const validateForm = async (data, isEdit = false) => {
        const newErrors = {};

        // Name validation
        if (!data.name.trim()) {
            newErrors.name = 'Classroom name is required';
        } else if (data.name.length > 255) {
            newErrors.name = 'Classroom name must not exceed 255 characters';
        } else {
            // Check uniqueness on the same floor
            try {
                const response = await axiosInstance.get(`${basePath}/check-name`, {
                    params: {
                        name: data.name,
                        floor: data.floor,
                        excludeId: isEdit && selectedClassroom ? selectedClassroom.classroomId : undefined,
                    },
                });
                if (response.data?.exists) {
                    newErrors.name = 'This classroom name already exists on the same floor';
                }
            } catch (error) {
                console.error('Error checking classroom name:', error);
            }
        }

        // Floor validation
        if (!data.floor.trim()) {
            newErrors.floor = 'Floor is required';
        } else if (data.floor.length > 50) {
            newErrors.floor = 'Floor must not exceed 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle Add Modal
    const openAddModal = () => {
        setFormData({ name: '', floor: '' });
        setErrors({});
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setIsAddModalOpen(false);
        setFormData({ name: '', floor: '' });
        setErrors({});
    };

    // Handle Edit Modal
    const openEditModal = (classroom) => {
        setSelectedClassroom(classroom);
        setFormData({
            name: classroom.name,
            floor: classroom.floor,
        });
        setErrors({});
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedClassroom(null);
        setFormData({ name: '', floor: '' });
        setErrors({});
    };

    // Handle Details Modal
    const openDetailsModal = (classroom) => {
        setSelectedClassroom(classroom);
        setIsDetailsModalOpen(true);
    };

    const closeDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedClassroom(null);
    };

    // Handle Form Submit
    const handleSubmit = async (e, isEdit = false) => {
        e.preventDefault();
        
        const isValid = await validateForm(formData, isEdit);
        if (!isValid) return;

        setIsSubmitting(true);

        if (isEdit && selectedClassroom) {
            axiosInstance
                .put(`${basePath}/${selectedClassroom.classroomId}`, formData)
                .then((res) => {
                    const updated = res.data?.classroom;
                    setClassrooms((prev) =>
                        prev.map((c) =>
                            c.classroomId === selectedClassroom.classroomId
                                ? updated || { ...c, ...formData }
                                : c
                        )
                    );
                    setFlash({ success: res.data?.message || 'Classroom updated successfully' });
                    closeEditModal();
                })
                .catch((error) => {
                    console.error('Error updating classroom:', error);
                    const serverMessage = error.response?.data?.message;
                    if (serverMessage) {
                        setFlash({ error: serverMessage });
                    } else {
                        setFlash({ error: 'Failed to update classroom' });
                    }
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        } else {
            axiosInstance
                .post(basePath, formData)
                .then((res) => {
                    const created = res.data?.classroom;
                    setClassrooms((prev) => (created ? [...prev, created] : prev));
                    setFlash({ success: res.data?.message || 'Classroom created successfully' });
                    closeAddModal();
                })
                .catch((error) => {
                    console.error('Error creating classroom:', error);
                    const serverMessage = error.response?.data?.message;
                    if (serverMessage) {
                        setFlash({ error: serverMessage });
                    } else {
                        setFlash({ error: 'Failed to create classroom' });
                    }
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    // Handle Delete
    const handleDelete = (classroom) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete classroom "${classroom.name}" on ${classroom.floor}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance
                    .delete(`${basePath}/${classroom.classroomId}`)
                    .then((res) => {
                        setClassrooms((prev) => prev.filter((c) => c.classroomId !== classroom.classroomId));
                        setFlash({ success: res.data?.message || 'Classroom deleted successfully' });
                    })
                    .catch((error) => {
                        console.error('Error deleting classroom:', error);
                        const serverMessage = error.response?.data?.message;
                        if (serverMessage) {
                            setFlash({ error: serverMessage });
                        } else {
                            setFlash({ error: 'Failed to delete classroom' });
                        }
                    });
            }
        });
    };

    // Handle Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    return (
        <DashboardLayout title="Classrooms">

            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Classrooms</h1>
                        <p className="text-gray-600 mt-1">Manage classroom locations and availability</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Classroom
                    </button>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Total Classrooms</div>
                        <div className="text-3xl font-bold text-indigo-600">{stats.total}</div>
                    </div>
                    {uniqueFloors.slice(0, 3).map((floor) => (
                        <div key={floor} className="bg-white rounded-lg shadow p-6">
                            <div className="text-sm text-gray-600">{floor}</div>
                            <div className="text-3xl font-bold text-green-600">{stats.byFloor[floor]}</div>
                        </div>
                    ))}
                </div>

                {/* Search and Filter */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            placeholder="Search classrooms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </div>
                    <select
                        value={floorFilter}
                        onChange={(e) => setFloorFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                        <option value="all">All Floors</option>
                        {uniqueFloors.map((floor) => (
                            <option key={floor} value={floor}>
                                {floor}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Classrooms Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    #
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Classroom Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Floor
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created At
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredClassrooms.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                                        No classrooms found
                                    </td>
                                </tr>
                            ) : (
                                filteredClassrooms.map((classroom, index) => (
                                    <tr key={classroom.classroomId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {index + 1}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                            {classroom.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {classroom.floor}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {new Date(classroom.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                            <button
                                                onClick={() => openDetailsModal(classroom)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => openEditModal(classroom)}
                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(classroom)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Add Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800">Add New Classroom</h2>
                            </div>
                            <form onSubmit={(e) => handleSubmit(e, false)}>
                                <div className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Classroom Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                errors.name ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Room 101, Lab A"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Floor <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="floor"
                                            value={formData.floor}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                errors.floor ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Ground Floor, 1st Floor"
                                        />
                                        {errors.floor && (
                                            <p className="mt-1 text-sm text-red-500">{errors.floor}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={closeAddModal}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create Classroom'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {isEditModalOpen && selectedClassroom && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800">Edit Classroom</h2>
                            </div>
                            <form onSubmit={(e) => handleSubmit(e, true)}>
                                <div className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Classroom Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                errors.name ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Room 101, Lab A"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Floor <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="floor"
                                            value={formData.floor}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                errors.floor ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Ground Floor, 1st Floor"
                                        />
                                        {errors.floor && (
                                            <p className="mt-1 text-sm text-red-500">{errors.floor}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={closeEditModal}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update Classroom'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Details Modal */}
                {isDetailsModalOpen && selectedClassroom && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800">Classroom Details</h2>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Classroom Name</label>
                                    <p className="mt-1 text-lg font-semibold text-gray-900">{selectedClassroom.name}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Floor</label>
                                    <p className="mt-1">
                                        <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                                            {selectedClassroom.floor}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Created At</label>
                                    <p className="mt-1 text-gray-900">
                                        {new Date(selectedClassroom.createdAt).toLocaleString()}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                                    <p className="mt-1 text-gray-900">
                                        {new Date(selectedClassroom.updatedAt).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={closeDetailsModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Classrooms;
